import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DelegationService } from '../services/delegation-service';
import httpClient from '../services/http-client';
import {
  DelegationDto,
  CreateDelegationRequest,
  UpdateDelegationRequest,
  DelegationListResponse,
  ActiveDelegationDto
} from '../types';

const delegationService = new DelegationService(httpClient);

/**
 * Hook to get all delegations for the current user
 */
export function useMyDelegations(
  divisionId: string | undefined,
  activeOnly?: boolean,
  pageNumber: number = 1,
  pageSize: number = 50
) {
  return useQuery({
    queryKey: ['delegations', 'me', divisionId, activeOnly, pageNumber, pageSize],
    queryFn: () => delegationService.getMyDelegations(divisionId!, activeOnly, pageNumber, pageSize),
    enabled: !!divisionId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to get delegations given by the current user
 */
export function useMyDelegationsGiven(
  divisionId: string | undefined,
  activeOnly?: boolean,
  pageNumber: number = 1,
  pageSize: number = 50
) {
  return useQuery({
    queryKey: ['delegations', 'given', divisionId, activeOnly, pageNumber, pageSize],
    queryFn: () => delegationService.getMyDelegationsGiven(divisionId!, activeOnly, pageNumber, pageSize),
    enabled: !!divisionId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to get delegations received by the current user
 */
export function useMyDelegationsReceived(
  divisionId: string | undefined,
  activeOnly?: boolean,
  pageNumber: number = 1,
  pageSize: number = 50
) {
  return useQuery({
    queryKey: ['delegations', 'received', divisionId, activeOnly, pageNumber, pageSize],
    queryFn: () => delegationService.getMyDelegationsReceived(divisionId!, activeOnly, pageNumber, pageSize),
    enabled: !!divisionId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to get current active delegation
 */
export function useMyActiveDelegation(divisionId: string | undefined) {
  return useQuery({
    queryKey: ['delegations', 'active', divisionId],
    queryFn: () => delegationService.getMyActiveDelegation(divisionId!),
    enabled: !!divisionId,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to get a specific delegation by ID
 */
export function useDelegation(divisionId: string | undefined, delegationId: string | undefined) {
  return useQuery({
    queryKey: ['delegations', divisionId, delegationId],
    queryFn: () => delegationService.getDelegationById(divisionId!, delegationId!),
    enabled: !!divisionId && !!delegationId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to create a delegation
 */
export function useCreateDelegation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ divisionId, request }: { divisionId: string; request: CreateDelegationRequest }) =>
      delegationService.createDelegation(divisionId, request),
    onSuccess: (_, variables) => {
      // Invalidate all delegation queries for this division
      queryClient.invalidateQueries({ queryKey: ['delegations', 'me', variables.divisionId] });
      queryClient.invalidateQueries({ queryKey: ['delegations', 'given', variables.divisionId] });
      queryClient.invalidateQueries({ queryKey: ['delegations', 'active', variables.divisionId] });
    },
  });
}

/**
 * Hook to update a delegation
 */
export function useUpdateDelegation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      divisionId,
      delegationId,
      request,
    }: {
      divisionId: string;
      delegationId: string;
      request: UpdateDelegationRequest;
    }) => delegationService.updateDelegation(divisionId, delegationId, request),
    onSuccess: (_, variables) => {
      // Invalidate delegation queries
      queryClient.invalidateQueries({ queryKey: ['delegations', 'me', variables.divisionId] });
      queryClient.invalidateQueries({ queryKey: ['delegations', 'given', variables.divisionId] });
      queryClient.invalidateQueries({ queryKey: ['delegations', variables.divisionId, variables.delegationId] });
      queryClient.invalidateQueries({ queryKey: ['delegations', 'active', variables.divisionId] });
    },
  });
}

/**
 * Hook to deactivate a delegation
 */
export function useDeactivateDelegation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ divisionId, delegationId }: { divisionId: string; delegationId: string }) =>
      delegationService.deactivateDelegation(divisionId, delegationId),
    onSuccess: (_, variables) => {
      // Invalidate delegation queries
      queryClient.invalidateQueries({ queryKey: ['delegations', 'me', variables.divisionId] });
      queryClient.invalidateQueries({ queryKey: ['delegations', 'given', variables.divisionId] });
      queryClient.invalidateQueries({ queryKey: ['delegations', variables.divisionId, variables.delegationId] });
      queryClient.invalidateQueries({ queryKey: ['delegations', 'active', variables.divisionId] });
    },
  });
}

/**
 * Hook to delete a delegation
 */
export function useDeleteDelegation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ divisionId, delegationId }: { divisionId: string; delegationId: string }) =>
      delegationService.deleteDelegation(divisionId, delegationId),
    onSuccess: (_, variables) => {
      // Invalidate delegation queries
      queryClient.invalidateQueries({ queryKey: ['delegations', 'me', variables.divisionId] });
      queryClient.invalidateQueries({ queryKey: ['delegations', 'given', variables.divisionId] });
      queryClient.invalidateQueries({ queryKey: ['delegations', 'received', variables.divisionId] });
      queryClient.invalidateQueries({ queryKey: ['delegations', 'active', variables.divisionId] });
    },
  });
}

/**
 * Hook to get all active delegations for a division (Admin only)
 */
export function useActiveDelegations(divisionId: string | undefined) {
  return useQuery({
    queryKey: ['delegations', 'division-active', divisionId],
    queryFn: () => delegationService.getActiveDelegations(divisionId!),
    enabled: !!divisionId,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to get delegations for a specific user (Admin only)
 */
export function useUserDelegations(
  divisionId: string | undefined,
  userId: string | undefined,
  activeOnly?: boolean,
  pageNumber: number = 1,
  pageSize: number = 50
) {
  return useQuery({
    queryKey: ['delegations', 'user', divisionId, userId, activeOnly, pageNumber, pageSize],
    queryFn: () => delegationService.getUserDelegations(divisionId!, userId!, activeOnly, pageNumber, pageSize),
    enabled: !!divisionId && !!userId,
    staleTime: 30 * 1000,
  });
}
